import { Button, Input, InputNumber, Popover, Radio, Select } from 'antd';
import React, { useState, useMemo } from 'react';
import { DocService } from '../../../../services/dataService';
import { useQuery } from 'react-query';
import { twMerge } from 'tailwind-merge';
import { useFilters } from '../../../../hooks/filters';

const Badge = ({ count, className }) => {
  return (
    <span className={twMerge('text-xs text-neutral-500 ml-2 bg-neutral-100 px-1 rounded-md', className)}>{count}</span>
  );
};

const Label = ({ children }) => {
  return <div className="text-sm text-neutral-500 font-medium">{children}</div>;
};

// Helper function to convert year range to array of years
const rangeToYears = (minYear, maxYear) => {
  if (!minYear || !maxYear) return [];
  const years = [];
  for (let year = parseInt(minYear); year <= parseInt(maxYear); year++) {
    years.push(year.toString());
  }
  return years;
};

// Helper function to convert array of years to range
const yearsToRange = (years) => {
  if (!years || !Array.isArray(years) || years.length === 0) {
    return { min: null, max: null };
  }
  const numericYears = years.map((y) => parseInt(y)).filter((y) => !isNaN(y));
  if (numericYears.length === 0) {
    return { min: null, max: null };
  }
  return {
    min: Math.min(...numericYears),
    max: Math.max(...numericYears),
  };
};

const Filter = ({ onClear, filters, onFilterChange }) => {
  const filterSuggestions = useQuery(['filter-suggestions'], () => DocService.getFilters());

  const buckets = filterSuggestions.data?.buckets;
  if (!buckets) return null;

  // Get available years from buckets for min/max constraints
  const availableYears = useMemo(() => {
    if (!buckets['published_year']) return [];
    return buckets['published_year']
      .map((item) => parseInt(item.key))
      .filter((year) => !isNaN(year))
      .sort((a, b) => a - b);
  }, [buckets]);

  const minAvailableYear = availableYears.length > 0 ? availableYears[0] : null;
  const maxAvailableYear = availableYears.length > 0 ? availableYears[availableYears.length - 1] : null;

  // Convert current filter to range format
  const yearRange = useMemo(() => {
    return yearsToRange(filters['published_year']);
  }, [filters['published_year']]);

  // Handle year range changes
  const handleYearRangeChange = (type, value) => {
    const newRange = {
      ...yearRange,
      [type]: value,
    };

    // Ensure min <= max
    if (newRange.min && newRange.max && newRange.min > newRange.max) {
      // If min > max, swap them
      [newRange.min, newRange.max] = [newRange.max, newRange.min];
    }

    // If both min and max are set, convert to array of years
    if (newRange.min && newRange.max) {
      const years = rangeToYears(newRange.min, newRange.max);
      onFilterChange({ ...filters, published_year: years });
    } else if (newRange.min || newRange.max) {
      // If only one is set, store as single year
      const year = newRange.min || newRange.max;
      onFilterChange({ ...filters, published_year: [year.toString()] });
    } else {
      // If both are cleared, set to empty array
      onFilterChange({ ...filters, published_year: [] });
    }
  };
  return (
    <div className="relative">
      <div className="flex flex-col gap-4 max-w-[500px] max-h-[400px] overflow-y-scroll p-4 pb-8">
        <div className="absolute -top-8 right-0 z-[100]">
          <Button type="link" onClick={onClear}>
            Clear All
          </Button>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="w-full basis-full">
            <Label>Authors</Label>
            <Select
              mode="tags"
              placeholder="Enter Authors"
              className="w-full"
              value={filters.authors}
              onChange={(value) => {
                onFilterChange({ ...filters, authors: value });
              }}
            >
              {buckets.authors?.map((item) => (
                <Select.Option key={item.key} value={item.key}>
                  {item.key}
                  <Badge count={item.doc_count} />
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="w-full basis-full">
            <Label>Drug</Label>
            <Select
              mode="tags"
              placeholder="Enter Drug"
              className="w-full"
              value={filters.drug}
              onChange={(value) => {
                onFilterChange({ ...filters, drug: value });
              }}
            >
              {buckets.drug?.map((item) => (
                <Select.Option key={item.key} value={item.key}>
                  {item.key}
                  <Badge count={item.doc_count} />
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="w-full basis-full">
            <Label>NCCN Cancer Type</Label>
            <Select
              mode="tags"
              placeholder="Enter Tissue Type"
              className="w-full"
              value={filters['disease'] || filters['task_specific_details.nccn_cancer_types']}
              onChange={(value) => {
                const newFilters = { ...filters };
                newFilters['disease'] = value;
                // Remove old key if present
                delete newFilters['task_specific_details.nccn_cancer_types'];
                onFilterChange(newFilters);
              }}
            >
              {(buckets['disease.raw'] || buckets['task_specific_details.nccn_cancer_types'])?.map((item) => (
                <Select.Option key={item.key} value={item.key}>
                  {item.key}
                  <Badge count={item.doc_count} />
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="w-full basis-full">
            <Label>Tissue Type</Label>
            <Select
              mode="tags"
              placeholder="Enter Tissue Type"
              className="w-full"
              value={filters['task_specific_details.tissue_type']}
              onChange={(value) => {
                onFilterChange({
                  ...filters,
                  'task_specific_details.tissue_type': value,
                });
              }}
            >
              {buckets['task_specific_details.tissue_type']?.map((item) => (
                <Select.Option key={item.key} value={item.key}>
                  {item.key}
                  <Badge count={item.doc_count} />
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="basis-6/12 w-full">
            <Label>Relevance</Label>
            <Radio.Group
              defaultValue=""
              buttonStyle="solid"
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  is_relevant: e.target.value,
                });
              }}
              value={filters['is_relevant']}
            >
              <Radio.Button value={true}>Include</Radio.Button>
              <Radio.Button value={false}>Exclude</Radio.Button>
              <Radio.Button value={null}>All</Radio.Button>
            </Radio.Group>
          </div>
          <div className="basis-5/12">
            <Label>Validated</Label>
            <Radio.Group
              defaultValue=""
              buttonStyle="solid"
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  is_validated: e.target.value,
                });
              }}
              value={filters['is_validated']}
            >
              <Radio.Button value={true}>Yes</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
              <Radio.Button value={null}>All</Radio.Button>
            </Radio.Group>
          </div>

          <div className="basis-6/12">
            <Label>Included in Review</Label>
            <Radio.Group
              defaultValue=""
              buttonStyle="solid"
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  included_in_review: e.target.value,
                });
              }}
              value={filters['included_in_review']}
            >
              <Radio.Button value={true}>Yes</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
              <Radio.Button value={null}>All</Radio.Button>
            </Radio.Group>
          </div>

          <div className="basis-5/12">
            <Label>Study Type</Label>
            <Select
              mode="tags"
              placeholder="Enter Study Type"
              className="w-full"
              value={filters['study_type']}
              onChange={(value) => {
                onFilterChange({ ...filters, study_type: value });
              }}
            >
              {buckets['study_type']?.map((item) => (
                <Select.Option key={item.key} value={item.key}>
                  {item.key}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="basis-full">
            <Label>Therapeutic Association</Label>
            <Radio.Group
              defaultValue=""
              buttonStyle="solid"
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  impact_type: e.target.value,
                });
              }}
              value={filters['impact_type']}
            >
              <Radio.Button value="Positive">Positive</Radio.Button>
              <Radio.Button value="No Benefit">No Benefit</Radio.Button>
              <Radio.Button value="Negative">Negative</Radio.Button>
              <Radio.Button value="Inconclusive">Inconclusive</Radio.Button>
              <Radio.Button value="Not mentioned">Not Mentioned</Radio.Button>
              <Radio.Button value={null}>All</Radio.Button>
            </Radio.Group>
          </div>
          <div className="basis-full">
            <Label>Publication Year</Label>
            <div className="flex gap-2 items-center">
              <InputNumber
                placeholder="From"
                min={minAvailableYear}
                max={maxAvailableYear}
                value={yearRange.min}
                onChange={(value) => handleYearRangeChange('min', value)}
                className="w-full"
                controls={true}
              />
              <span className="text-neutral-400">to</span>
              <InputNumber
                placeholder="To"
                min={minAvailableYear}
                max={maxAvailableYear}
                value={yearRange.max}
                onChange={(value) => handleYearRangeChange('max', value)}
                className="w-full"
                controls={true}
              />
            </div>
          </div>

          <div className="basis-full">
            <Label>Combination</Label>
            <Radio.Group
              defaultValue=""
              buttonStyle="solid"
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  is_combination_therapy: e.target.value,
                });
              }}
              value={filters['is_combination_therapy']}
            >
              <Radio.Button value={true}>Yes</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
              <Radio.Button value={null}>All</Radio.Button>
            </Radio.Group>
          </div>
          <div className="basis-full">
            <Label>Import Date</Label>
            <Select
              mode="multiple"
              placeholder="Select Import Date"
              className="w-full"
              value={filters['import_date']}
              onChange={(value) => {
                onFilterChange({ ...filters, import_date: value });
              }}
            >
              {buckets['import_date']
                ?.slice()
                .sort((a, b) => {
                  // Sort import dates in descending order (newest first)
                  // Dates are typically in YYYY-MM-DD format, so string comparison works
                  return b.key.localeCompare(a.key);
                })
                .map((item) => (
                  <Select.Option key={item.key} value={item.key}>
                    {item.key}
                  </Select.Option>
                ))}
            </Select>
          </div>
          <div className="basis-6/12 w-full">
            <Label>First level filter</Label>
            <Radio.Group
              defaultValue=""
              buttonStyle="solid"
              onChange={(e) => {
                onFilterChange({
                  ...filters,
                  first_level_check: e.target.value,
                });
              }}
              value={filters['first_level_check']}
            >
              <Radio.Button value={true}>True</Radio.Button>
              <Radio.Button value={false}>False</Radio.Button>
              <Radio.Button value={null}>All</Radio.Button>
            </Radio.Group>
          </div>
          <div className="basis-5/12">
            <Label>Is Generic</Label>
            <Radio.Group
              defaultValue=""
              buttonStyle="solid"
              onChange={(e) => {
                const value = e.target.value;
                // Handle boolean values - Ant Design Radio might convert them to strings
                let filterValue;
                if (value === true || value === 'true') {
                  filterValue = true;
                } else if (value === false || value === 'false') {
                  filterValue = false;
                } else {
                  filterValue = null; // Clear filter for "All"
                }
                onFilterChange({
                  ...filters,
                  'scoring_meta.is_generic': filterValue,
                });
              }}
              value={filters['scoring_meta.is_generic']}
            >
              <Radio.Button value={true}>Yes</Radio.Button>
              <Radio.Button value={false}>No</Radio.Button>
              <Radio.Button value={null}>All</Radio.Button>
            </Radio.Group>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SearchFilter({ search, setSearch, filters, setFilters }) {
  const filterContext = useFilters();
  const resolvedSearch = search ?? filterContext.search;
  const resolvedSetSearch = setSearch ?? filterContext.setSearch;
  const resolvedFilters = filters ?? filterContext.filters;
  const resolvedSetFilters = setFilters ?? filterContext.setFilters;
  const [currentSearch, setCurrentSearch] = useState(resolvedSearch || '');
  // This is to cache the filter suggestions
  const _ = useQuery(['filter-suggestions'], () => DocService.getFilters());

  const onClear = () => {
    setCurrentSearch('');
    resolvedSetSearch('');
    // Reset filters to empty object; caller can decide defaults
    resolvedSetFilters({});
  };

  const activeFilters = Object.keys(resolvedFilters || {}).filter(
    (key) =>
      resolvedFilters[key] !== null &&
      resolvedFilters[key] !== '' &&
      !(Array.isArray(resolvedFilters[key]) && resolvedFilters[key].length === 0),
  ).length;

  return (
    <div>
      <div className="flex gap-2">
        <Input
          placeholder="Search"
          value={currentSearch ? currentSearch : resolvedSearch}
          onPressEnter={(e) => {
            resolvedSetSearch(e.target.value);
          }}
          onChange={(e) => {
            if (e.target.value == '') {
              // clear the search
              onClear();
            } else {
              setCurrentSearch(e.target.value);
            }
          }}
        />

        <Popover
          title="Filters"
          content={
            <Filter
              filters={resolvedFilters || {}}
              onFilterChange={(filters) => {
                resolvedSetFilters((prev) => ({ ...(prev || {}), ...filters }));
              }}
              onClear={onClear}
            />
          }
          trigger="click"
        >
          <Button>
            Filters
            {activeFilters > 0 && <Badge count={activeFilters} className="text-blue-500 bg-blue-100" />}
          </Button>
        </Popover>
      </div>
    </div>
  );
}
